/**
 * 화면이 쓰는 시간대 — **시스템 기본 + 프로젝트별 재정의** (서버 services/time_zones 와 같은 규칙).
 *
 *   · 시스템 기본: 서버의 OPENFLOW_TZ (없으면 Asia/Seoul) — 앱이 뜰 때 받는다
 *   · 실제로 쓰는 값: 지금 연 프로젝트가 정해 두었으면 그것, 아니면 시스템 기본
 *
 * 날짜 표시(formatters)와 DAG 변환 요청(withTimeZone)이 **같은 값**을 쓴다. 두 곳이 따로 정하면
 * 화면에 보이는 스케줄 시각과 실제로 도는 시각이 어긋난다(예전에 UTC 로 돌던 것처럼).
 *
 * 스토어가 아니라 모듈 값인 까닭: formatters 는 컴포넌트 밖(서비스·도우미)에서도 불린다.
 * 값은 스토어가 바뀔 때 TimeZoneSync 가 여기로 옮겨 준다.
 */
export const DEFAULT_TIME_ZONE = 'Asia/Seoul';

let systemTz = DEFAULT_TIME_ZONE;
let effectiveTz = DEFAULT_TIME_ZONE;

export function isValidTimeZone(tz: unknown): tz is string {
  if (!tz || typeof tz !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const systemTimeZone = () => systemTz;
export const effectiveTimeZone = () => effectiveTz;

export function setTimeZones(system?: string | null, project?: string | null): void {
  systemTz = isValidTimeZone(system) ? system : DEFAULT_TIME_ZONE;
  effectiveTz = isValidTimeZone(project) ? project : systemTz;
}

/**
 * DAG 변환에 보내는 워크플로 설정에 시간대를 싣는다 — 스케줄(cron)을 이 시간대로 해석하게.
 * 배포·최종 실행 조회·DAG 코드 보기가 모두 이것을 거쳐야 같은 코드가 나온다(다르면 '바뀐 워크플로' 로 보인다).
 * 캔버스에는 저장하지 않는다 — 프로젝트 설정을 바꾸면 워크플로마다 고치지 않아도 따라가야 한다.
 */
export function withTimeZone<T extends object | null | undefined>(settings: T): T & { timeZone: string } {
  return { ...(settings || {}), timeZone: effectiveTz } as T & { timeZone: string };
}

/** 시간대 목록 — 브라우저가 아는 전체, 없으면 자주 쓰는 것만 */
export function timeZoneOptions(): string[] {
  const common = ['Asia/Seoul', 'UTC', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Ho_Chi_Minh',
    'Asia/Kolkata', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles'];
  const all: string[] = typeof (Intl as any).supportedValuesOf === 'function' ? (Intl as any).supportedValuesOf('timeZone') : [];
  return Array.from(new Set([...common, ...all]));
}
